import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Helmet } from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"
import { useLocation } from "@reach/router"
import useMediaQuery from "../hooks/useMediaQuery"

function DefaultHelmet({
  description,
  lang,
  meta,
  title,
  keywords,
  microdata,
  structuredData,
  imageUrl = null,
  appendPostfixToTitle = true,
}) {
  const titlePostfix = " | UTD"

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            siteUrl
            author
            keywords
            imageUrl
          }
        }
      }
    `
  )

  const location = useLocation()
  const metaDescription = cropString(
    description || site?.siteMetadata?.description,
    160
  )
  const canonicalUrl = buildCanonicalUrl(site?.siteMetadata?.siteUrl, location)
  const pageTitle = cropString(
    title || site?.siteMetadata?.title,
    70 - (appendPostfixToTitle ? titlePostfix.length : 0)
  )
  const metaKeywords = keywords || site?.siteMetadata?.keywords
  const isExtraSmall = useMediaQuery("(max-width: 767px)")
  const isSmall = useMediaQuery("(max-width: 991px)")
  const isMedium = useMediaQuery("(max-width: 1199px)")

  const metaTags = [
    {
      name: `description`,
      content: metaDescription,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:title`,
      content: pageTitle,
    },
    {
      property: `og:url`,
      content: canonicalUrl,
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      name: "keywords",
      content: metaKeywords,
    },
    {
      name: "viewport",
      content: "width=device-width, initial-scale=1, maximum-scale=1",
    },
  ]

  if (site?.siteMetadata?.imageUrl || imageUrl) {
    metaTags.push({
      property: `og:image`,
      content: site?.siteMetadata?.imageUrl || imageUrl,
    })
  }

  useEffect(() => {
    const targetClass = "iubenda-tp-btn"
    const beaconButtonClass = "BeaconFabButtonFrame"
    const mobileBeaconGap = 16
    const refreshDelays = [100, 500, 1000, 2000, 3000]
    const observedElements = []
    const refreshTimeouts = []
    let resizeObserver
    let isRefreshingAfterWidgetLoad = false

    function observeElementResize(element) {
      if (
        !element ||
        !window.ResizeObserver ||
        observedElements.includes(element)
      ) {
        return
      }

      if (!resizeObserver) {
        resizeObserver = new ResizeObserver(() => {
          applyStyles()
        })
      }

      resizeObserver.observe(element)
      observedElements.push(element)
    }

    function refreshAfterWidgetLoad() {
      if (isRefreshingAfterWidgetLoad) {
        return
      }

      isRefreshingAfterWidgetLoad = true
      refreshDelays.forEach(delay => {
        refreshTimeouts.push(window.setTimeout(applyStyles, delay))
      })
    }

    function applyStyles() {
      const btn = document.querySelector(`button.${targetClass}`)

      if (!btn) {
        return
      }

      const beaconButton = document.querySelector(`.${beaconButtonClass}`)
      observeElementResize(btn)
      observeElementResize(beaconButton)

      const beaconContainer = document.querySelector("#beacon-container")
        ?.childElementCount
      let bottomValue = beaconContainer ? "190px" : "90px"
      let rightValue = "var(--hs-beacon-h-offset-right, 40px)"

      if (isSmall) {
        bottomValue = "96px"
      } else if (isMedium) {
        bottomValue = "150px"
      }

      if (isExtraSmall && beaconButton) {
        refreshAfterWidgetLoad()

        const buttonRect = btn.getBoundingClientRect()
        const beaconRect = beaconButton.getBoundingClientRect()
        const beaconBottomOffset = Math.max(
          window.innerHeight - beaconRect.bottom,
          0
        )
        const centeredBottomOffset =
          beaconBottomOffset +
          Math.max((beaconRect.height - buttonRect.height) / 2, 0)

        rightValue = `${Math.max(
          window.innerWidth - beaconRect.left + mobileBeaconGap,
          mobileBeaconGap
        )}px`
        bottomValue = `${centeredBottomOffset}px`
      }

      btn.style.setProperty("left", "unset", "important")
      btn.style.setProperty("right", rightValue, "important")
      btn.style.setProperty("bottom", bottomValue, "important")
      btn.style.setProperty("margin-right", "0", "important")
      btn.style.setProperty("margin-bottom", "0", "important")
      btn.style.setProperty("z-index", "1049", "important")
    }

    const observer = new MutationObserver(() => {
      applyStyles()
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    applyStyles()
    window.addEventListener("resize", applyStyles)

    return () => {
      observer.disconnect()
      resizeObserver?.disconnect()
      refreshTimeouts.forEach(timeout => window.clearTimeout(timeout))
      window.removeEventListener("resize", applyStyles)
    }
  }, [isExtraSmall, isSmall, isMedium])

  const jsonLd = structuredData || microdata

  return (
    <Helmet
      htmlAttributes={{ lang }}
      title={appendPostfixToTitle ? pageTitle + titlePostfix : pageTitle}
      meta={metaTags.concat(meta)}
    >
      {jsonLd
        ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map(
            (schema, index) => (
              <script key={index} type="application/ld+json">
                {serializeJsonLd(schema)}
              </script>
            )
          )
        : null}

      <link rel="canonical" href={canonicalUrl} />

      {/* GDPR */}
      {/** Privacy Controls and Cookie Solution */}
      {/* The autoblocker must initialize before GTM and other tracking scripts. */}
      <script
        type="text/javascript"
        src="//embeds.iubenda.com/widgets/a2f1d01e-8b3f-489d-97a6-52e5d4552384.js"
      />

      {/** Consent Database */}
      <script type="text/javascript">
        {`var _iub = _iub || {}; _iub.cons_instructions = _iub.cons_instructions || []; _iub.cons_instructions.push(["init", {api_key: "C71qqe5NmU2T7AZUEHCyKGP3xwPw5ifM"}]);`}
      </script>
      <script
        type="text/javascript"
        src="https://cdn.iubenda.com/cons/iubenda_cons.js"
        async
      />
      {/* END GDPR */}
    </Helmet>
  )
}

DefaultHelmet.defaultProps = {
  lang: `en`,
  meta: [],
  description: ``,
}

DefaultHelmet.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.object),
  structuredData: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  title: PropTypes.string.isRequired,
}

const serializeJsonLd = schema => JSON.stringify(schema).replace(/</g, "\\u003c")

const cropString = (str, length) => {
  if (str.length <= length) {
    return str
  }
  return str.substring(0, length - 5) + "..."
}

const buildCanonicalUrl = (siteUrl, location) => {
  if (!siteUrl && location?.href) {
    return location.href
  }

  const baseUrl = (siteUrl || "").replace(/\/+$/, "")
  const pathname = location?.pathname || "/"
  const normalizedPathname = pathname.startsWith("/")
    ? pathname
    : `/${pathname}`

  return `${baseUrl}${normalizedPathname}`
}

export default DefaultHelmet
